/**
 * FTL Living Interface — status colors (single source of truth).
 *
 * Replaces the scattered local STATUS_COLORS maps (Projects had its own, nav
 * rows had colored chips, etc.). Every status dot/pill is "bioluminescent" — a
 * semantic color with a matching glow — so status reads instantly + consistently.
 *
 * Values reference the @theme status tokens in index.css.
 */
export type StatusTone = "success" | "warning" | "error" | "info" | "neutral" | "brand";

interface ToneSpec {
  /** CSS var for the dot/text fill. */
  color: string;
  /** CSS var (or rgba) for the surrounding glow. */
  glow: string;
  /** Tailwind-friendly faint background for pills. */
  bgClass: string;
  textClass: string;
}

export const STATUS_TONES: Record<StatusTone, ToneSpec> = {
  success: { color: "var(--color-success)", glow: "var(--color-success-glow, rgb(16 185 129 / 0.45))", bgClass: "bg-[var(--color-success)]/10", textClass: "text-[var(--color-success)]" },
  warning: { color: "var(--color-warning)", glow: "var(--color-warning-glow, rgb(245 158 11 / 0.45))", bgClass: "bg-[var(--color-warning)]/10", textClass: "text-[var(--color-warning)]" },
  error: { color: "var(--color-error)", glow: "var(--color-error-glow, rgb(239 68 68 / 0.45))", bgClass: "bg-[var(--color-error)]/10", textClass: "text-[var(--color-error)]" },
  info: { color: "var(--color-info)", glow: "var(--color-info-glow, rgb(59 122 255 / 0.45))", bgClass: "bg-[var(--color-info)]/10", textClass: "text-[var(--color-info)]" },
  brand: { color: "var(--color-brand-600)", glow: "rgb(29 95 245 / 0.45)", bgClass: "bg-brand-50", textClass: "text-brand-600" },
  neutral: { color: "var(--color-text-tertiary)", glow: "rgb(100 116 139 / 0.30)", bgClass: "bg-[var(--color-surface-sunken)]", textClass: "text-[var(--color-text-secondary)]" },
};

/** Map common domain statuses → a tone (the ONE place this mapping lives). */
const STATUS_TONE_MAP: Record<string, StatusTone> = {
  active: "success", in_progress: "info", baselined: "brand", draft: "neutral",
  archived: "neutral", blocked: "error", at_risk: "warning", review_ready: "info",
  committed: "success", failed: "error", succeeded: "success", pending: "warning",
  provisioning: "info",
};

export function toneForStatus(status: string | null | undefined): StatusTone {
  if (!status) return "neutral";
  return STATUS_TONE_MAP[status.toLowerCase()] ?? "neutral";
}
